import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { tokenize, type Token } from '@vif/core';
import {
  CALIBRATION_SKIP_WPM,
  CALIBRATION_START,
  calibrationStep,
  type CalibrationChoice,
  type CalibrationState,
} from '../app/calibration.js';
import { READER_FONT_SIZES_PX } from '../app/settings.js';
import { useServices } from '../app/services.js';
import { WordDisplay } from '../components/WordDisplay.jsx';

/** Prototype's rotating calibration samples (one per round). */
const SAMPLE_TEXTS = [
  'The ocean stretches endlessly toward the horizon where sky meets water in a seamless band of blue. Waves roll gently against the shore carrying shells and smooth stones from distant places. Seabirds circle overhead riding invisible currents of warm air rising from the sunlit sand below.',
  'Mountains rise like ancient sentinels watching over valleys carved by rivers over countless centuries. Snow clings to their highest peaks even in summer creating a stark contrast against deep green forests. Hikers follow winding trails through meadows filled with wildflowers that bloom briefly each spring.',
  'Cities pulse with energy from dawn until well past midnight as millions of people move through streets and buildings. Markets overflow with fresh produce and handmade goods while cafes fill with conversation and laughter. Every corner holds a story waiting to be discovered by those who take the time to look.',
  'Stars emerge slowly as twilight fades painting the sky with countless points of ancient light. Each one burns millions of miles away yet appears close enough to touch on clear mountain nights. Astronomers have mapped these lights for centuries finding patterns that guided sailors across unknown seas.',
];

const sampleTokens: Token[][] = SAMPLE_TEXTS.map((text) =>
  tokenize({ lang: 'en', blocks: [{ kind: 'paragraph', text }] }),
);

type Step = 'welcome' | 'calibrate' | 'result' | 'tips';

export function OnboardingView(): ReactNode {
  const { t, settings, updateSettings } = useServices();
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('welcome');
  const [calibration, setCalibration] = useState<CalibrationState>(CALIBRATION_START);
  const [wordIdx, setWordIdx] = useState<number | null>(null);
  const [roundOver, setRoundOver] = useState(false);
  const stageRef = useRef<HTMLDivElement>(null);
  const rafRef = useRef<number>(0);

  const tokens = sampleTokens[calibration.round % sampleTokens.length]!;

  const playSample = useCallback((wpm: number, round: number) => {
    cancelAnimationFrame(rafRef.current);
    setRoundOver(false);
    setWordIdx(0);
    const words = sampleTokens[round % sampleTokens.length]!;
    const interval = 60000 / wpm;
    let last: number | null = null;
    let acc = 0;
    let idx = 0;
    const frame = (now: number): void => {
      if (last !== null) acc += now - last;
      last = now;
      while (acc >= interval) {
        acc -= interval;
        idx++;
        if (idx >= words.length) {
          setWordIdx(null);
          setRoundOver(true);
          return;
        }
        setWordIdx(idx);
      }
      rafRef.current = requestAnimationFrame(frame);
    };
    rafRef.current = requestAnimationFrame(frame);
  }, []);

  useEffect(() => () => cancelAnimationFrame(rafRef.current), []);

  const finish = useCallback(
    (wpm: number) => {
      updateSettings({ targetWpm: wpm, onboardingComplete: true });
      setStep('tips');
    },
    [updateSettings],
  );

  const choose = useCallback(
    (choice: CalibrationChoice) => {
      cancelAnimationFrame(rafRef.current);
      const next = calibrationStep(calibration, choice);
      setCalibration(next);
      if (next.done) {
        setStep('result');
      } else {
        playSample(next.wpm, next.round);
      }
    },
    [calibration, playSample],
  );

  const anchorX = (stageRef.current?.clientWidth ?? 620) * 0.38;

  return (
    <div className="app">
      <div className="onboarding">
        {step === 'welcome' && (
          <div className="fade-up" style={{ display: 'grid', gap: 20, justifyItems: 'center' }}>
            <h1>
              <em style={{ color: 'var(--accent)', fontStyle: 'normal' }}>Vif</em>
            </h1>
            <p className="lede">{t('onboarding.intro')}</p>
            <button
              className="btn btn-primary fade-up-d1"
              onClick={() => {
                setStep('calibrate');
                setCalibration(CALIBRATION_START);
                playSample(CALIBRATION_START.wpm, 0);
              }}
            >
              {t('onboarding.getStarted')}
            </button>
            <span
              className="skip-link fade-up-d2"
              onClick={() => {
                updateSettings({ targetWpm: CALIBRATION_SKIP_WPM, onboardingComplete: true });
                void navigate('/library');
              }}
            >
              {t('onboarding.skip')}
            </span>
          </div>
        )}

        {step === 'calibrate' && (
          <div
            className="fade-up"
            style={{ display: 'grid', gap: 20, justifyItems: 'center', width: '100%' }}
          >
            <p className="kicker">
              {t('onboarding.round', { round: calibration.round + 1, wpm: calibration.wpm })}
            </p>
            <div className="sample-stage" ref={stageRef}>
              {wordIdx !== null && tokens[wordIdx] !== undefined ? (
                <WordDisplay
                  tokens={[tokens[wordIdx]!]}
                  font={settings.font}
                  fontSizePx={READER_FONT_SIZES_PX[1]}
                  anchorX={anchorX}
                  layout="vertical"
                  showGuides={settings.showGuides}
                  orpColor={settings.orpColor}
                />
              ) : (
                !roundOver && (
                  <p style={{ color: 'var(--textDim)', padding: 60, textAlign: 'center' }}>
                    {t('onboarding.preparing')}
                  </p>
                )
              )}
            </div>
            {roundOver && (
              <div className="fade-up" style={{ display: 'grid', gap: 12, justifyItems: 'center' }}>
                <p style={{ color: 'var(--textMuted)' }}>{t('onboarding.howWasThat')}</p>
                <div
                  style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'center' }}
                >
                  <button className="btn btn-sm" onClick={() => choose('fast')}>
                    {t('onboarding.tooFast')}
                  </button>
                  <button className="btn btn-sm btn-primary" onClick={() => choose('right')}>
                    {t('onboarding.justRight')}
                  </button>
                  <button className="btn btn-sm" onClick={() => choose('slow')}>
                    {t('onboarding.tooSlow')}
                  </button>
                  {calibration.round > 0 && (
                    <button className="btn btn-sm" onClick={() => choose('faster')}>
                      {t('onboarding.faster')}
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {step === 'result' && (
          <div className="fade-up" style={{ display: 'grid', gap: 16, justifyItems: 'center' }}>
            <div className="wpm-big">
              {calibration.wpm} <span>{t('reader.wpm')}</span>
            </div>
            <p className="lede">{t('onboarding.yourSpeed')}</p>
            <button className="btn btn-primary" onClick={() => finish(calibration.wpm)}>
              {t('onboarding.continue')}
            </button>
          </div>
        )}

        {step === 'tips' && (
          <div
            className="fade-up"
            style={{ display: 'grid', gap: 24, justifyItems: 'center', width: '100%' }}
          >
            <h2 style={{ fontSize: '1.5rem' }}>{t('onboarding.tipsTitle')}</h2>
            <div className="tips">
              {(
                [
                  ['onboarding.tipOrpTitle', 'onboarding.tipOrpBody'],
                  ['onboarding.tipKeysTitle', 'onboarding.tipKeysBody'],
                  ['onboarding.tipWarmupTitle', 'onboarding.tipWarmupBody'],
                  ['onboarding.tipDocsTitle', 'onboarding.tipDocsBody'],
                ] as const
              ).map(([title, body], i) => (
                <div className={`tip fade-up-d${Math.min(i, 2)}`} key={title}>
                  <div>
                    <div className="t-title">{t(title)}</div>
                    <div className="t-body">{t(body)}</div>
                  </div>
                </div>
              ))}
            </div>
            <button className="btn btn-primary" onClick={() => void navigate('/library')}>
              {t('onboarding.startReading')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
